/**
 * The page's paths, relative to the site's root: `start/<config>/` for a
 * configuration, `p/<id>` for a kept project, `<set>/<path>` for an example
 * link, and a configuration with either of the last two. The server serves
 * the document for each of them; see "Project URLs" and "Example Links" in the
 * page's README.
 */

/** The example sets a link can name: ESP-IDF's, and the site's own. */
export type ExampleSetName = "esp-idf" | "builtin";

/**
 * The page's own part of a path, after the site's prefix. An example's path
 * has slashes and takes the rest of the path. The document's base script
 * matches the same forms.
 */
const PAGE =
    /\/(?:start\/([^/]+(?:\/[^/]+)?)(?:\/p\/([^/]+)|\/(esp-idf|builtin)\/(.+?))?|p\/([^/]+)|(esp-idf|builtin)\/(.+?))\/?$/;

/** The configuration of a page path, `<site>/start/<name>` or `<site>/start/<owner>/<repo>`; none at the site's root. */
export function configurationName(pathname: string): string | undefined {
    const match = PAGE.exec(pathname);
    return match?.[1] === undefined ? undefined : decoded(match[1]);
}

/** The kept project a page path identifies, `<site>[/start/<name>]/p/<id>`. */
export function projectIdOf(pathname: string): string | undefined {
    const match = PAGE.exec(pathname);
    const id = match?.[2] ?? match?.[5];
    return id === undefined ? undefined : decoded(id);
}

/** The example a page path links to, `<site>[/start/<name>]/<set>/<path>`. */
export function exampleOf(pathname: string): { set: ExampleSetName; path: string } | undefined {
    const match = PAGE.exec(pathname);
    const set = (match?.[3] ?? match?.[6]) as ExampleSetName | undefined;
    const path = match?.[4] ?? match?.[7];
    if (set === undefined || path === undefined) return undefined;
    return { set, path: path.split("/").map(decoded).join("/") };
}

/** The path, relative to the site's root, of the page with `config` and the project `id`. */
export function pagePath(config: string | undefined, id: string | undefined): string {
    // A configuration on GitHub is "<owner>/<repo>", two segments of the path.
    const start = config === undefined ? "" : `start/${config.split("/").map(encodeURIComponent).join("/")}/`;
    const project = id === undefined ? "" : `p/${encodeURIComponent(id)}`;
    return start + project || "./";
}

/**
 * The path, relative to the site's root, of the link to the example `path` in
 * `set` with `config`. A kept copy of the example records it as its source.
 */
export function exampleLink(config: string | undefined, set: ExampleSetName, path: string): string {
    const start = pagePath(config, undefined).replace(/^\.\/$/, "");
    return `${start}${set}/${path.split("/").map(encodeURIComponent).join("/")}`;
}

/** A configuration's start example, `<set>/<path>`. */
export function startExampleOf(text: string): { set: ExampleSetName; path: string } | undefined {
    const match = /^(esp-idf|builtin)\/((?:[^/]+\/)*[^/]+)$/.exec(text);
    return match === null ? undefined : { set: match[1] as ExampleSetName, path: match[2]! };
}

/**
 * A new project's id: its name, with what a path would need escaped replaced
 * by `-`, and six random characters, so that two copies of one example differ.
 */
export function newProjectId(name: string, random: () => number = Math.random): string {
    const slug = name
        .replace(/[^A-Za-z0-9_.-]+/g, "-")
        .replace(/^[-.]+|-+$/g, "")
        .slice(0, 40);
    let suffix = "";
    for (let i = 0; i < 6; i++) suffix += Math.floor(random() * 36).toString(36);
    return slug === "" ? suffix : `${slug}-${suffix}`;
}

function decoded(part: string): string {
    try {
        return decodeURIComponent(part);
    } catch {
        return part;
    }
}
